// services/donation-analytics.service.ts

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, tap, of, map, catchError } from 'rxjs';
import { environment } from 'src/environments/environment';

// ========================================
// INTERFACES
// ========================================

export interface DonationChartData {
  historical: Array<{
    date: string;
    amount: number;
    amount_in_euros: number;
    donor_count: number;
  }>;
  period: string;
  totalAmount: number;
  totalAmountInEuros: number;
  totalDonors: number;
  averageDonation: number;
  averageDonationInEuros: number;
}

export interface DonationChartResponse {
  success: boolean;
  chartData: DonationChartData;
}

// Cache Entry Interface
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  period?: string;
}

@Injectable({
  providedIn: 'root'
})
export class DonationAnalyticsService {
  private baseUrl = `${environment.apiUrl}/business/donations`;
  
  // Cache subjects for real-time updates
  private chartDataCache$ = new BehaviorSubject<DonationChartData | null>(null);
  
  public chartData$ = this.chartDataCache$.asObservable();

  // Cache storage with TTL
  private cache = new Map<string, CacheEntry<any>>();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  constructor(private http: HttpClient) {}

  /**
   * Check if cache entry is still valid
   */
  private isCacheValid(key: string, period?: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;

    const now = Date.now();
    const isExpired = (now - entry.timestamp) > this.CACHE_TTL;
    const periodMatches = !period || entry.period === period;

    return !isExpired && periodMatches;
  }

  /**
   * Set cache entry
   */
  private setCache<T>(key: string, data: T, period?: string): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      period
    });
  }

  /**
   * Get cache entry
   */
  private getCache<T>(key: string): T | null {
    const entry = this.cache.get(key);
    return entry ? entry.data : null;
  }

  /**
   * Create empty donation chart data structure
   */
  private createEmptyChartData(period: string = '30d'): DonationChartData {
    return {
      historical: [],
      period: period,
      totalAmount: 0,
      totalAmountInEuros: 0,
      totalDonors: 0,
      averageDonation: 0,
      averageDonationInEuros: 0
    };
  }

  // ========================================
  // API METHODS
  // ========================================

  /**
   * Get donation chart data for business hub
   * @param period '7d' | '30d' | '90d' | 'all'
   * @param forceRefresh Skip cache and force API call
   */
  getChartData(
    period: string = '30d', 
    forceRefresh: boolean = false
  ): Observable<DonationChartData> {
    const cacheKey = `chart_${period}`;
    
    // Check cache first
    if (!forceRefresh && this.isCacheValid(cacheKey, period)) {
      const cached = this.getCache<DonationChartData>(cacheKey);
      if (cached) {
        this.chartDataCache$.next(cached);
        return of(cached);
      }
    }

    const params = new HttpParams().set('period', period);
    
    return this.http.get<DonationChartResponse>(`${this.baseUrl}/hub/chart-data`, { params }).pipe(
      map(response => {
        // ✅ Extract data from nested chartData structure
        const data = response.chartData || response;
        
        // ✅ Ensure all required fields exist with defaults
        const chartData: DonationChartData = {
          historical: data.historical || [],
          period: data.period || period,
          totalAmount: data.totalAmount || 0,
          totalAmountInEuros: data.totalAmountInEuros || 0,
          totalDonors: data.totalDonors || 0,
          averageDonation: data.averageDonation || 0,
          averageDonationInEuros: data.averageDonationInEuros || 0
        };
        
        // Update cache and subject
        this.setCache(cacheKey, chartData, period);
        this.chartDataCache$.next(chartData);
        
        return chartData;
      }),
      catchError(error => {
        console.error('Error fetching donation chart data:', error);
        
        // ✅ Return empty structure on error
        const emptyData = this.createEmptyChartData(period);
        this.chartDataCache$.next(emptyData);
        
        return of(emptyData);
      })
    );
  }

  // ========================================
  // UTILITY METHODS
  // ========================================

  /**
   * Refresh donation data
   */
  refreshData(period: string = '30d'): void {
    this.getChartData(period, true).subscribe();
  }

  /**
   * Invalidate specific cache entry
   */
  invalidateCache(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Invalidate cache for specific period
   */
  invalidatePeriod(period: string): void {
    const keysToDelete: string[] = [];
    
    this.cache.forEach((_, key) => {
      if (key.includes(period)) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Clear all cache
   */
  clearCache(): void {
    this.cache.clear();
    this.chartDataCache$.next(null);
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats(): { size: number; entries: string[] } {
    const entries: string[] = [];
    this.cache.forEach((value, key) => {
      const age = Date.now() - value.timestamp;
      const ageMinutes = Math.floor(age / 60000);
      entries.push(`${key} (${ageMinutes}m old, period: ${value.period || 'none'})`);
    });

    return {
      size: this.cache.size,
      entries
    };
  }

  /**
   * Format euros from cents
   */
  formatEuros(cents: number): string {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(cents / 100);
  }

  /**
   * Format euros (already in euros)
   */
  formatEurosFromEuros(euros: number): string {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(euros);
  }

  /**
   * Check if donation data is empty
   */
  isEmptyData(data: DonationChartData | null): boolean {
    return !data || 
           !data.historical || 
           data.historical.length === 0 || 
           data.totalDonors === 0;
  }

  /**
   * Get donation trend (positive/negative/neutral)
   */
  getDonationTrend(data: DonationChartData): 'up' | 'down' | 'neutral' {
    if (!data.historical || data.historical.length < 2) {
      return 'neutral';
    }

    const recent = data.historical.slice(-7); // Last 7 days
    const older = data.historical.slice(-14, -7); // Previous 7 days

    const recentTotal = recent.reduce((sum, day) => sum + day.amount, 0);
    const olderTotal = older.reduce((sum, day) => sum + day.amount, 0);

    if (recentTotal > olderTotal) return 'up';
    if (recentTotal < olderTotal) return 'down';
    return 'neutral';
  }
}